import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

import {
  computeSplinePath,
  dayMonth,
  integerTicks,
  rangeSubtitle,
} from "../chartScale";
import { count } from "../../Insights/format";

// top leaves room for the peak's value pill, drawn 27px above its point.
const PAD = { left: 30, right: 30, top: 32, baseline: 170 };
const CHART_HEIGHT = 215;

const useContainerWidth = () => {
  const [width, setWidth] = useState(0);
  const containerRef = useRef(null);

  const measure = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const clientW = containerRef.current.clientWidth;
      const w = Math.round(rect.width || clientW || 0);
      if (w > 0) {
        setWidth((prev) => (prev === w ? prev : w));
      }
    }
  }, []);

  const setRef = useCallback((node) => {
    containerRef.current = node;
    if (node) {
      const rect = node.getBoundingClientRect();
      const clientW = node.clientWidth;
      const w = Math.round(rect.width || clientW || 0);
      if (w > 0) {
        setWidth((prev) => (prev === w ? prev : w));
      }
    }
  }, []);

  useLayoutEffect(() => {
    measure();

    const node = containerRef.current;
    if (!node) return undefined;

    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const cr = entry.contentRect;
          const w = Math.round(cr?.width || entry.target?.clientWidth || 0);
          if (w > 0) {
            setWidth((prev) => (prev === w ? prev : w));
          }
        }
      });
      observer.observe(node);
    }

    window.addEventListener("resize", measure);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  return [setRef, width];
};

const orderWord = (n) => (Number(n) === 1 ? "order" : "orders");

export default function OrdersBars({ values }) {
  const [plotRef, measured] = useContainerWidth();
  const [activeDetail, setActiveDetail] = useState(null);
  const [tableOpen, setTableOpen] = useState(false);

  const series = Array.isArray(values) ? values : null;
  const loading = series === null;
  const counts = (series || []).map((day) => Number(day.count) || 0);
  const total = counts.reduce((sum, c) => sum + c, 0);
  const empty = !loading && series.length === 0;

  const fallbackWidth =
    typeof window !== "undefined" && window.innerWidth
      ? Math.max(340, Math.min(window.innerWidth - 60, 1100))
      : 800;

  const width = measured || fallbackWidth;
  const effectiveWidth = Math.max(340, width);
  const plotW = Math.max(0, effectiveWidth - PAD.left - PAD.right);
  const plotH = PAD.baseline - PAD.top;

  const subtitle = series && series.length
    ? rangeSubtitle(series[0].date, series[series.length - 1].date)
    : "Completed orders per day";

  const dailyAvg = series && series.length
    ? (total / series.length).toFixed(1)
    : "0.0";

  const maxVal = Math.max(0, ...counts);
  const ticks = integerTicks(maxVal);
  const topTick = ticks[ticks.length - 1] || 1;
  const yOf = (val) => PAD.baseline - (val / topTick) * plotH;

  let primaryPeakIdx = -1;
  if (maxVal > 0) {
    counts.forEach((c, i) => {
      if (c === maxVal && primaryPeakIdx === -1) primaryPeakIdx = i;
    });
  }

  let secondaryPeakIdx = -1;
  if (maxVal > 0) {
    let secMax = 0;
    counts.forEach((c, i) => {
      if (i !== primaryPeakIdx && c > secMax) {
        secMax = c;
        secondaryPeakIdx = i;
      }
    });
    if (secMax < 2) secondaryPeakIdx = -1;
  }

  const points = counts.map((c, i) => ({
    x: Math.round(
      (PAD.left + (counts.length > 1 ? (i / (counts.length - 1)) * plotW : plotW / 2)) *
        10
    ) / 10,
    y: Math.round(yOf(c) * 10) / 10,
  }));

  const linePath = computeSplinePath(points, PAD.baseline, PAD.top);
  const areaPath =
    points.length > 1
      ? `${linePath} L ${points[points.length - 1].x} ${PAD.baseline} L ${points[0].x} ${PAD.baseline} Z`
      : "";

  const handlePointClick = (day, val, idx) => {
    if (activeDetail && activeDetail.index === idx) {
      setActiveDetail(null);
    } else {
      setActiveDetail({ date: dayMonth(day.date), count: val, index: idx });
    }
  };

  const handlePointHover = (day, val, idx) => {
    setActiveDetail({ date: dayMonth(day.date), count: val, index: idx });
  };

  const toggleTableView = () => {
    setTableOpen((prev) => !prev);
  };

  return (
    <main
      className="orders-analytics-card bg-white rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-slate-100 overflow-hidden"
      data-purpose="orders-analytics-card"
    >
      {/* Card Header */}
      <section className="orders-card-header p-5 pb-2" data-purpose="card-header">
        {loading ? (
          <>
            <div className="orders-header-top flex items-start justify-between gap-2">
              <div>
                <div
                  className="orders-skeleton-text"
                  style={{ width: 140, height: 24, borderRadius: 6 }}
                />
                <div
                  className="orders-skeleton-text"
                  style={{ width: 90, height: 14, marginTop: 6, borderRadius: 4 }}
                />
              </div>
              <div
                className="orders-skeleton-text"
                style={{ width: 120, height: 26, borderRadius: 9999 }}
              />
            </div>
            <div className="orders-quick-summary-grid grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-slate-100/80">
              <div className="orders-quick-stat-card orders-skeleton-card" />
              <div className="orders-quick-stat-card orders-skeleton-card" />
            </div>
          </>
        ) : (
          <>
            <div className="orders-header-top flex items-start justify-between gap-2">
              <div>
                <h1 className="orders-card-title text-xl font-bold tracking-tight text-slate-900">
                  Completed orders per day
                </h1>
                <p className="orders-card-subtitle text-xs font-medium text-slate-400 mt-0.5 tracking-wide">
                  {subtitle} · delivered only, cancelled left out
                </p>
              </div>
              {/* Stat Badge */}
              <div className="orders-stat-badge inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100/90 text-slate-600 border border-slate-200/50 text-[11px] font-semibold tracking-wider uppercase">
                {count(total)} DELIVERED IN TOTAL
              </div>
            </div>

            {/* Quick Summary Stats */}
            <div className="orders-quick-summary-grid grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-slate-100/80">
              <div className="orders-quick-stat-card bg-slate-50/70 p-2.5 rounded-xl border border-slate-100">
                <span className="orders-stat-label text-[11px] font-medium text-slate-400 uppercase tracking-wider block">
                  Peak Day
                </span>
                <div className="orders-stat-val-row flex items-baseline gap-1.5 mt-0.5">
                  <span className="orders-stat-num text-lg font-bold text-slate-900">
                    {maxVal}
                  </span>
                  <span className="orders-stat-sub-brand text-xs text-brand-500 font-semibold">
                    {primaryPeakIdx >= 0 && maxVal > 0
                      ? dayMonth(series[primaryPeakIdx].date)
                      : "—"}
                  </span>
                </div>
              </div>
              <div className="orders-quick-stat-card bg-slate-50/70 p-2.5 rounded-xl border border-slate-100">
                <span className="orders-stat-label text-[11px] font-medium text-slate-400 uppercase tracking-wider block">
                  Daily Avg
                </span>
                <div className="orders-stat-val-row flex items-baseline gap-1.5 mt-0.5">
                  <span className="orders-stat-num text-lg font-bold text-slate-900">
                    {dailyAvg}
                  </span>
                  <span className="orders-stat-sub-muted text-xs text-slate-500 font-medium">
                    delivered/day
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      {/* BEGIN: InteractiveChartSection */}
      <section
        className="orders-chart-section px-3 pt-3 pb-2 relative"
        data-purpose="chart-container"
        ref={plotRef}
      >
        {loading ? (
          <div
            style={{
              height: `${CHART_HEIGHT}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              className="orders-skeleton-wave"
              data-testid="orders-skeleton-wave"
            />
          </div>
        ) : empty ? (
          <div style={{ height: `${CHART_HEIGHT}px`, position: "relative" }}>
            <p className="dash-chart-empty">No completed orders in this period</p>
          </div>
        ) : (
          <div className="orders-chart-canvas-wrap relative w-full overflow-hidden select-none">
            <svg
              aria-label={`Completed orders per day line chart, ${subtitle}. Highest ${count(
                maxVal
              )} ${orderWord(maxVal)} on ${
                primaryPeakIdx >= 0 ? dayMonth(series[primaryPeakIdx].date) : ""
              }.`}
              className="orders-chart-svg w-full overflow-visible"
              height={CHART_HEIGHT}
              preserveAspectRatio="xMidYMid meet"
              role="img"
              viewBox={`0 0 ${effectiveWidth} ${CHART_HEIGHT}`}
              style={{ width: "100%", height: `${CHART_HEIGHT}px` }}
            >
              <defs>
                {/* Gradient under curve */}
                <linearGradient id="areaGradient" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#ff5436" stopOpacity="0.32" />
                  <stop offset="60%" stopColor="#ff5436" stopOpacity="0.08" />
                  <stop offset="100%" stopColor="#ff5436" stopOpacity="0.0" />
                </linearGradient>
                {/* Subtle line glow */}
                <filter height="140%" id="glow" width="140%" x="-20%" y="-20%">
                  <feDropShadow
                    dx="0"
                    dy="3"
                    floodColor="#ff5436"
                    floodOpacity="0.25"
                    stdDeviation="3"
                  />
                </filter>
              </defs>

              {/* Horizontal Guide Lines & Y-Axis Labels */}
              {ticks.map((tick) => (
                <g key={tick} className="guide-line text-slate-400">
                  <text
                    className="orders-axis-text"
                    fill="#94a3b8"
                    fontSize="12"
                    fontWeight="500"
                    textAnchor="start"
                    x="5"
                    y={yOf(tick) + 4}
                  >
                    {tick}
                  </text>
                  <line
                    stroke={tick === 0 ? "#cbd5e1" : "#e2e8f0"}
                    strokeDasharray={tick === 0 ? undefined : "3 3"}
                    strokeWidth={tick === 0 ? 1.2 : 1}
                    x1="26"
                    x2={effectiveWidth - 10}
                    y1={yOf(tick)}
                    y2={yOf(tick)}
                  />
                </g>
              ))}

              {/* Gradient Area Fill */}
              {areaPath && (
                <path d={areaPath} fill="url(#areaGradient)" />
              )}

              {/* Smooth Curved Metric Line */}
              {linePath && (
                <path
                  d={linePath}
                  fill="none"
                  filter="url(#glow)"
                  stroke="#ff5436"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="3"
                />
              )}

              {/* Highlight Rings for Peaks */}
              {primaryPeakIdx >= 0 && maxVal > 0 && (
                <circle
                  className="pulse-ring"
                  cx={points[primaryPeakIdx].x}
                  cy={points[primaryPeakIdx].y}
                  fill="#ff5436"
                  fillOpacity="0.2"
                  r="10"
                />
              )}
              {secondaryPeakIdx >= 0 && (
                <circle
                  className="pulse-ring"
                  cx={points[secondaryPeakIdx].x}
                  cy={points[secondaryPeakIdx].y}
                  fill="#ff5436"
                  fillOpacity="0.2"
                  r="8"
                />
              )}

              {/* Data Point Markers */}
              {points.map((pt, idx) => {
                const val = counts[idx];
                const day = series[idx];
                const isPeak1 = idx === primaryPeakIdx && maxVal > 0;
                const isPeak2 = idx === secondaryPeakIdx;
                const isFloatingPill = isPeak1 || isPeak2;
                const pillBg = isPeak1 ? "#ff5436" : "#0f172a";
                const pillW = val >= 10 ? 32 : isPeak2 ? 30 : 26;
                const pillH = 19;
                const pillX = pt.x - pillW / 2;
                const pillY = isPeak1 ? pt.y - 27 : pt.y - 26;
                const textY = isPeak1 ? pt.y - 14 : pt.y - 13;

                return (
                  <g
                    key={day.date || idx}
                    data-testid="orders-bar"
                    tabIndex={0}
                    aria-label={`${dayMonth(day.date)}: ${count(val)} ${orderWord(val)}`}
                    className="cursor-pointer"
                    onClick={() => handlePointClick(day, val, idx)}
                    onMouseEnter={() => handlePointHover(day, val, idx)}
                    onFocus={() => handlePointHover(day, val, idx)}
                  >
                    <circle
                      className="chart-point"
                      cx={pt.x}
                      cy={pt.y}
                      fill="#ffffff"
                      r={isFloatingPill ? 4.5 : 3.5}
                      stroke="#ff5436"
                      strokeWidth={isFloatingPill ? 2.5 : 2}
                    />

                    {isFloatingPill ? (
                      <>
                        <rect
                          fill={pillBg}
                          height={pillH}
                          rx={4.5}
                          width={pillW}
                          x={pillX}
                          y={pillY}
                        />
                        <text
                          className="orders-pill-text"
                          fill="#ffffff"
                          fontSize="13"
                          fontWeight="700"
                          textAnchor="middle"
                          x={pt.x}
                          y={textY}
                        >
                          {val}
                        </text>
                      </>
                    ) : (
                      val > 0 && (
                        <text
                          className="orders-point-val-text"
                          fill="#334155"
                          fontSize="13"
                          fontWeight="600"
                          textAnchor="middle"
                          x={pt.x}
                          y={pt.y - 8}
                        >
                          {val}
                        </text>
                      )
                    )}
                  </g>
                );
              })}

              {/* X-Axis Labels */}
              <g
                className="orders-xaxis-group tracking-tighter"
                fill="#64748b"
                fontSize="11.5"
                fontWeight="500"
                textAnchor="middle"
              >
                {points.map((pt, idx) => (
                  <text
                    key={idx}
                    className="orders-date-label"
                    x={pt.x}
                    y="188"
                  >
                    {dayMonth(series[idx].date)}
                  </text>
                ))}
              </g>
            </svg>
          </div>
        )}

        {/* Dynamic Tap Feedback Hint */}
        {!loading && !empty && (
          <div
            className="orders-feedback-hint mt-1 text-center text-[11px] text-slate-400 font-medium py-1 bg-slate-50/60 rounded-lg flex items-center justify-center gap-1.5 border border-slate-100"
            id="interactiveFeedback"
          >
            <svg
              className="orders-feedback-icon w-3 h-3 text-brand-500 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
            <span id="feedbackText">
              {activeDetail ? (
                <>
                  <span className="font-bold text-slate-800">
                    {activeDetail.date}:
                  </span>{" "}
                  <span className="text-brand-600 font-bold">
                    {count(activeDetail.count)}
                  </span>{" "}
                  {orderWord(activeDetail.count)}
                </>
              ) : (
                "Tap points to inspect daily totals"
              )}
            </span>
          </div>
        )}
      </section>
      {/* END: InteractiveChartSection */}

      {/* BEGIN: TableToggleSection */}
      {!loading && !empty && series && series.length > 0 && (
        <footer
          className="orders-table-footer border-t border-slate-100 bg-slate-50/40 p-4"
          data-purpose="toggle-action-container"
        >
          <button
            aria-controls="ordersTableView"
            aria-expanded={tableOpen}
            className="orders-table-toggle-btn w-full flex items-center justify-between py-2 text-xs font-semibold text-slate-600 hover:text-brand-600 transition-colors focus:outline-none"
            id="tableToggleButton"
            type="button"
            onClick={toggleTableView}
          >
            <span className="orders-table-toggle-title flex items-center gap-1.5">
              <svg
                className={`orders-table-chevron w-3.5 h-3.5 text-slate-500 transform transition-transform duration-200 ease-out ${
                  tableOpen ? "rotate-90" : ""
                }`}
                fill="currentColor"
                id="chevronIcon"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
              <span id="toggleText">
                {tableOpen ? "Hide table view" : "View as table"}
              </span>
            </span>
            <span className="orders-table-records-count text-[11px] text-slate-400 font-normal">
              {series.length} records
            </span>
          </button>

          {/* Expandable Table Container */}
          <div
            className={`orders-table-view-container mt-3 transition-all duration-300 ease-in-out ${
              tableOpen ? "" : "hidden"
            }`}
            data-purpose="data-table-view"
            id="ordersTableView"
            style={{ display: tableOpen ? "block" : "none" }}
          >
            <div className="orders-table-inner-card rounded-xl border border-slate-200 overflow-hidden bg-white shadow-xs">
              <table className="orders-data-table w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-medium uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="py-2.5 px-3">Date</th>
                    <th className="py-2.5 px-3 text-right">Delivered</th>
                    <th className="py-2.5 px-3 text-right">Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 font-normal">
                  {series.map((day, idx) => {
                    const val = counts[idx];
                    const isPeak1 = idx === primaryPeakIdx && maxVal > 0;
                    const isPeak2 = idx === secondaryPeakIdx;
                    const share =
                      total > 0
                        ? `${((val / total) * 100).toFixed(1)}%`
                        : "0.0%";

                    const rowClass = isPeak1
                      ? "orders-row-peak-primary bg-orange-50/30 font-medium"
                      : isPeak2
                      ? "orders-row-peak-secondary bg-slate-50/60 font-medium"
                      : "";

                    return (
                      <tr key={day.date || idx} className={rowClass}>
                        <td
                          className={`py-2 px-3 ${
                            isPeak1 || isPeak2
                              ? "text-slate-900 font-medium"
                              : "text-slate-700"
                          }`}
                        >
                          {dayMonth(day.date)}
                        </td>
                        <td
                          className={`py-2 px-3 text-right ${
                            isPeak1
                              ? "text-brand-600 font-bold"
                              : isPeak2
                              ? "text-slate-900 font-bold"
                              : val === 0
                              ? "text-slate-400"
                              : ""
                          }`}
                        >
                          {count(val)}
                        </td>
                        <td
                          className={`py-2 px-3 text-right ${
                            val === 0 ? "text-slate-400" : "text-slate-500"
                          }`}
                        >
                          {share}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50 font-semibold border-t border-slate-200 text-slate-900">
                  <tr>
                    <td className="py-2 px-3">Total</td>
                    <td className="py-2 px-3 text-right text-brand-600">
                      {count(total)}
                    </td>
                    <td className="py-2 px-3 text-right">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </footer>
      )}
      {/* END: TableToggleSection */}
    </main>
  );
}

export { OrdersBars as OrdersChart };
